import { useEffect } from 'react'
import { useTier } from './tiers'

/**
 * Cards/panels that take a cursor-tracked highlight (U8 promotion).
 * The warm content cards (`.zone-card`/`.glass-card`) and the FUI
 * instrument panels (`.fui-panel`) — the surfaces an operator hovers.
 */
const GLOW_SELECTOR = '.zone-card, .glass-card, .fui-panel'

/**
 * Cursor-tracked glow (R20/R22): ONE delegated `pointermove` listener
 * (the MagneticFx precedent — never a listener per card, which would tank
 * perf across every surface) writes the pointer position as element-scoped
 * CSS custom properties (`--glowx`/`--glowy`, in %) on the hovered card and
 * flags it with `data-fx-glow`. `effects.css` paints a radial highlight
 * that follows the pointer and declares those vars with
 * `@property … { inherits: false }` so each update stays scoped to the one
 * card and never forces a style recalc across the tree (the CSS-variable
 * inheritance bomb the research digest flagged).
 *
 * Mounted by the shell next to MagneticFx; inert in Work Mode / under PRM
 * (the tier is already PRM-clamped) and on coarse pointers — the recipe is
 * itself gated `:not(.tier-work)`, this guard just stops the listener and
 * any var writes from running at all there.
 */
export function CursorGlow() {
  const { isWork } = useTier()

  useEffect(() => {
    if (isWork) return
    if (typeof window === 'undefined') return
    if (!window.matchMedia('(pointer: fine)').matches) return

    let raf = 0
    let current: HTMLElement | null = null
    let nextX = 0
    let nextY = 0

    function release() {
      if (current) {
        current.style.removeProperty('--glowx')
        current.style.removeProperty('--glowy')
        current.removeAttribute('data-fx-glow')
        current = null
      }
    }

    function onMove(e: PointerEvent) {
      const hit = (e.target as Element | null)?.closest?.(GLOW_SELECTOR) as HTMLElement | null
      if (hit !== current) {
        release()
        // The glow recipe sets `position: relative` on [data-fx-glow] to
        // establish a containing block for the ::after highlight. For an
        // element that is already absolutely/fixed/sticky-positioned (e.g.
        // the Ask-Hermes ResponsePanel anchored over the map), that override
        // (specificity 0,3,1) BEATS the element's own `position: absolute`
        // and reinterprets its left/top/transform offsets in the static
        // scheme → the panel teleports out from under the pointer → release →
        // back → re-acquire → flip again every frame (the "spazz"). So only
        // ever host the glow on elements whose computed position is `static`
        // or `relative`, where forcing `relative` is a positional no-op.
        // Checked ONCE per new hit (not per move) for perf.
        if (hit) {
          const pos = getComputedStyle(hit).position
          if (pos === 'absolute' || pos === 'fixed' || pos === 'sticky') {
            current = null
            return
          }
        }
        current = hit
        current?.setAttribute('data-fx-glow', '')
      }
      if (!current) return
      const r = current.getBoundingClientRect()
      if (r.width === 0 || r.height === 0) return
      // Pointer position as a percentage of the card — the radial origin.
      nextX = ((e.clientX - r.left) / r.width) * 100
      nextY = ((e.clientY - r.top) / r.height) * 100
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (current) {
          current.style.setProperty('--glowx', `${nextX}%`)
          current.style.setProperty('--glowy', `${nextY}%`)
        }
      })
    }

    function onLeaveDoc() {
      cancelAnimationFrame(raf)
      release()
    }

    document.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeaveDoc)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeaveDoc)
      cancelAnimationFrame(raf)
      release()
    }
  }, [isWork])

  return null
}
